import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';

import {ENTER, TAB} from '@angular/cdk/keycodes';
import {MatDialog} from '@angular/material/dialog';

import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, take} from 'rxjs/operators';

import {ChipPopupComponent, CountryCodeComponent} from 'src/app/components';
import {Activity, Device, Group, GroupMemberType, Individual, Message, Recipient} from 'src/app/models';
import {DevicesService, GroupsService, GroupTypeService, IndividualsService, SnackbarService} from 'src/app/services';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {FormControl} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-recipients',
    templateUrl: './recipients.component.html',
    styleUrls: ['./recipients.component.scss']
})
export class RecipientsComponent implements OnInit {
    @Input() recipients: Recipient[] = [];
    @Output() recipientsChange: EventEmitter<Recipient[]> = new EventEmitter();
    @Input() needsAck: boolean;
    @Input() showIndividualField: boolean;
    @Input() showGroupField: boolean;
    @Input() filterField: boolean;
    @Input() disableEditing: boolean;
    // MAT Chips
    selectable = true;
    removable = true;
    addOnBlur = true;
    separatorKeysCodes: number[] = [ENTER, TAB];
    // Auto completes
    individualsAndDevices = null;
    groups = null;
    phoneValid = true;
    individualChange = new Subject<string>();
    groupChange = new Subject<string>();
    groupTop = 460;
    groupPage = 1;
    currentPhone = null;
    currentGroup = '';
    groupHold = 0;
    foundGroup: any;
    autocompleteGroups = [];
    autocompleteGroupsFormatted = [];
    groupTypes: any;
    currentIndividual = '';
    foundIndividual: any;
    autocompleteIndFormatted = [];
    indTop = 460;
    indPage = 1;
    indHold = 0;
    groupTypeControl: FormControl;
    failedGroupLookups: string[] = [];
    totalLookups = 0;
    completedLookups = 0;
    @ViewChild('countryCode', {static: false}) countryCode: CountryCodeComponent;
    @ViewChild('individualInput', {static: false}) individualInput: ElementRef;
    @ViewChild('phoneInput', {static: false}) phoneInput: ElementRef;
    @ViewChild('groupInput', {static: false}) groupInput: ElementRef;
    @ViewChild('individualAutocomplete', {static: false}) individualAuto: MatAutocomplete;
    @ViewChild('groupAutocomplete', {static: false}) groupAuto: MatAutocomplete;
    @ViewChild(MatAutocompleteTrigger, {read: MatAutocompleteTrigger}) inputAutocomplete: MatAutocompleteTrigger;

    constructor(
        private individualsService: IndividualsService,
        private groupsService: GroupsService,
        private snackbarService: SnackbarService,
        private dialog: MatDialog,
        private deviceService: DevicesService,
        public groupTypeService: GroupTypeService,
        private route: ActivatedRoute) {
        this.groupTypeControl = new FormControl('');
    }

    private static individualToRecipient(event: any): Recipient {
        const recipient = new Recipient();
        recipient.recipient_type = <string>GroupMemberType.Individual;
        recipient.recipient_id = event.id;
        recipient.name = event.full_name;
        recipient.response_required = false;
        return recipient;
    }

    private static deviceToRecipient(event: any): Recipient {
        const recipient = new Recipient();
        recipient.recipient_type = <string>GroupMemberType.Device;
        recipient.name = event.name;
        recipient.recipient_id = event.id;
        recipient.response_required = false;
        return recipient;
    }

    private static groupToRecipient(event: any) {
        const holdRecipient = new Recipient();
        holdRecipient.recipient_type = <string>GroupMemberType.Group;
        holdRecipient.recipient_id = event.id;
        holdRecipient.name = event.name;
        holdRecipient.response_required = false;
        return holdRecipient;
    }

    ngOnInit() {
        this.route.queryParams.pipe(take(1)).subscribe((params: any) => {
            if (params['groupRecipients']) {
                const groupIds: string[] = params['groupRecipients'].split(',');
                this.totalLookups = groupIds.length;

                groupIds.forEach((groupId: string) => {

                    this.prePopulateOnCallGroupsFromUrl(groupId);
                });
            }
        });

        this.setupOnChangeSearches();
        this.groupTypeService.getGroupTypes().subscribe(gTypes => {
            this.groupTypes = gTypes;
        });
    }

    prePopulateOnCallGroupsFromUrl(groupId: string) {
        this.groupsService.getGroupNames(groupId).subscribe({
            next: (groups) => {
                const foundGroup = groups.find(e => e.id === +groupId);

                if (foundGroup) {
                    const recipient = RecipientsComponent.groupToRecipient(foundGroup);

                    recipient.ack_required = foundGroup.ack_required;
                    recipient.response_required = recipient.ack_required;
                    this.addRecipient(recipient, 'Duplicate Group provided - try again ');
                } else {
                    this.failedGroupLookups.push(groupId);
                }

                this.completedLookups++;

                if (this.completedLookups === this.totalLookups && this.failedGroupLookups.length > 0) {
                    this.snackbarService.openListSnackBar('Failed to find the following groups:', this.failedGroupLookups, true);
                    this.failedGroupLookups = [];
                    this.completedLookups = 0;
                    this.totalLookups = 0;
                }
            },
            error: (err) => {
                console.warn(err);
            }
        });
    }

    subTitle() {
        switch ((this.showGroupField ? 1 : 0) + (this.showIndividualField ? 1 : 0)) {
            case 1: {
                return this.showGroupField ? 'Group' : (this.showIndividualField ? 'Individual' : 'Phone Number');
            }
            case 2: {
                return this.showGroupField ? `Group or ${this.showIndividualField ? 'Individual' : 'Phone Number'}` : 'Individual or Phone Number';
            }
            case 3: {
                return 'Group, Individual, or Phone Number';
            }
        }
    }

    filterPhoneRecipient(recipient: Recipient) {
        return this.isType(recipient, GroupMemberType.Phone);
    }

    filterIndividualAndDeviceRecipient(recipient: Recipient) {
        return this.isType(recipient, GroupMemberType.Individual) || this.isType(recipient, GroupMemberType.Device);
    }

    filterGroupRecipient(recipient: Recipient) {
        return this.isType(recipient, GroupMemberType.Group);
    }

    isType(recipient: Recipient, type: any) {
        return recipient.recipient_type === type;
    }

    // chips code started
    acknowledgement(value: Recipient) {
        value.response_required = !value.response_required;
    }

    // This method clears error if the input is cleared or a valid phone number is entered
    // Advances on tab if no value is entered

    onInputTab(event, inputValue) {
        const value = event.target.value;
        if (event.key === 'Tab' && !value) {
            inputValue === 'individual' && !value ? this.individualInput.nativeElement.focus() : this.phoneInput.nativeElement.focus();
        }
    }

    resetInputField() {
        this.currentPhone = '';
        this.currentGroup = '';
        this.recipients = [];
        this.phoneValid = true;
    }

    addIndividual(event?: any): void {
        this.autocompleteIndFormatted = [];
        this.foundIndividual = this.individualsAndDevices.find(e => {
            return e.full_name !== undefined ? (e.full_name + ' (' + e.external_id + ')') === event : e.name === event;
        });
        this.onIndividualChanged('');
        this.addRecipient(
            this.foundIndividual.external_id ? RecipientsComponent.individualToRecipient(this.foundIndividual) : RecipientsComponent.deviceToRecipient(this.foundIndividual),
            'Duplicate Individual/Device provided - try again ');
        this.currentIndividual = '';
    }

    addIndividualReply(individualRecipient?: any): void {
        const recipient = new Recipient();
        recipient.recipient_type = <string>(!individualRecipient.device_id ? GroupMemberType.Individual : GroupMemberType.Device);
        recipient.recipient_id = (!individualRecipient.device_id ? individualRecipient.individual_id : individualRecipient.device_id);
        recipient.name = (!individualRecipient.device_id ? individualRecipient.individual_name : individualRecipient.channel_name);
        recipient.response_required = individualRecipient.response_required;
        this.addRecipient(recipient, 'Duplicate Individual/Device provided - try again ');
    }

    addGroup(event: any): void {
        this.autocompleteGroupsFormatted = [];

        this.foundGroup = this.groups.find(e => e.name === event);
        this.onGroupChanged('');

        const recipient = RecipientsComponent.groupToRecipient(this.foundGroup);
        recipient.ack_required = this.autocompleteGroups.find(group => group.name === event).ack_required;
        recipient.response_required = recipient.ack_required;

        this.addRecipient(recipient, 'Duplicate Group provided - try again ');

        this.currentGroup = '';
    }

    addGroupReply(groupRecipient: any): void {
        const holdRecipient = new Recipient();
        holdRecipient.recipient_type = <string>GroupMemberType.Group;
        holdRecipient.recipient_id = (!groupRecipient.super_group_id ? groupRecipient.group_id : groupRecipient.super_group_id);
        holdRecipient.name = (!groupRecipient.super_group_id ? groupRecipient.group : groupRecipient.super_group);
        holdRecipient.response_required = groupRecipient.response_required;
        holdRecipient.ack_required = groupRecipient.group_ack_required;
        if (!this.isDuplicate(holdRecipient)) {
            this.addRecipient(holdRecipient, 'Duplicate Group provided - try again ');
        }
    }

    isDuplicate(recipient: Recipient) {
        recipient.recipient_id = recipient.recipient_id.toString();
        const found = this.recipients.find(p => p.recipient_id === recipient.recipient_id && p.recipient_type === recipient.recipient_type);
        return Boolean(found);
    }

    remove(recipient: Recipient): void {
        this.recipients = this.recipients.filter((p: any) => (recipient.recipient_type !== p.recipient_type || recipient.recipient_id !== p.recipient_id));
        this.recipientsChange.emit(this.recipients);
    }

    /**
     * Initialize search Subject(s) with user input wait period.
     * Call search after wait period expires without additional user input.
     */
    setupOnChangeSearches() {
        const delay = 750;
        this.individualChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getIndividualsAndDevices(term));

        this.groupChange.pipe(
            debounceTime(delay),
            distinctUntilChanged()
        ).subscribe((term: string) => this.getGroupNames(term));
    }

    /**
     * Add Individual / Device search onChange event handler to trigger Subject
     */
    onIndividualChanged($event) {
        if ($event.length > 1) {
            const panel = this.individualAuto.panel.nativeElement;
            panel.addEventListener('scroll', event => this.onScroll(event, 'ind'));
        }
        this.individualChange.next($event);
    }

    /**
     * Actual Individual & Device Search call.
     * Get Individuals names for autocomplete
     */
    getIndividualsAndDevices(filterValueName: string) {
        this.currentIndividual = filterValueName;
        if (filterValueName != null && filterValueName.length > 1) {
            this.individualsService.getIndividualsAndDevices(filterValueName).subscribe(individAndDevices => {
                this.individualsAndDevices = individAndDevices.content;
                this.autocompleteIndFormatted = [];
                this.individualsAndDevices.forEach(e => {
                    this.autocompleteIndFormatted.push(e.full_name !== undefined ? e.full_name + ' (' + e.external_id + ')' : e.name);
                });
            });
        } else {
            this.individualsAndDevices = [];
            this.autocompleteIndFormatted = [];
        }
    }

    /**
     * Add Group search onChange event handler to trigger Subject
     */
    onGroupChanged($event: any) {
        if (this.currentGroup.length > 1) {
            const panel = this.groupAuto.panel.nativeElement;
            panel.addEventListener('scroll', event => this.onScroll(event, 'group'));
        }
        this.groupChange.next($event);
    }

    onScroll(event: any, type) {
        if (type === 'ind' && event.currentTarget.scrollTop !== this.indHold) {
            this.indHold = event.currentTarget.scrollTop;
            if (event.currentTarget.scrollTop < 460) {
                this.indTop = 460;
            }
            if (event.currentTarget.scrollTop > this.indTop) {
                this.indTop = this.indTop + 460;
                // Call pageable endpoint here, with a higher limitCount to get more values
                this.indPage = this.indPage + 1;
                this.loadInd(this.indPage);
            }
        }
        if (type === 'group' && event.currentTarget.scrollTop !== this.groupHold) {
            this.groupHold = event.currentTarget.scrollTop;
            if (event.currentTarget.scrollTop < 460) {
                this.groupTop = 460;
            }
            if (event.currentTarget.scrollTop > this.groupTop) {
                this.groupTop = this.groupTop + 460;
                // Call pageable endpoint here, with a higher limitCount to get more values
                this.groupPage = this.groupPage + 1;
                this.loadGroup(this.groupPage);
            }
        }
    }

    loadInd(page) {
        if (this.currentIndividual != null && this.currentIndividual.length > 1) {
            this.individualsService.getIndividualsAndDevicesPage(this.currentIndividual, page * 15).subscribe(res => {
                // @ts-ignore
                this.individualsAndDevices = <Individual[]>res;
                this.autocompleteIndFormatted = [];
                this.individualsAndDevices.forEach(e => {
                    this.autocompleteIndFormatted.push(e.full_name !== undefined ? e.full_name + ` (${e.external_id})` : e.name);
                });
            });
        } else {
            this.individualsAndDevices = [];
        }
    }

    loadGroup(page) {
        if (this.currentGroup != null && this.currentGroup.length > 1) {
            if (this.groupTypeControl.value > 0) {
                this.groupsService.getGroupNamesPageAndGroupTypeId(this.currentGroup, page * 15, this.groupTypeControl.value.toString(), Activity.ACTIVE)
                    .subscribe({
                        next: groups => {
                            this.groups = groups.content;
                            this.autocompleteGroupsFormatted = [];
                            this.groups.forEach(e => {
                                this.autocompleteGroupsFormatted.push(e.name);
                                this.groups.forEach(e => this.autocompleteGroups.push(e));
                            });
                        }
                    });
            } else {
                this.groupsService.getGroupNamesPage(this.currentGroup, page * 15)
                    .subscribe({
                        next: groups => {
                            this.groups = groups.content;
                            this.autocompleteGroupsFormatted = [];
                            this.groups.forEach(e => this.autocompleteGroupsFormatted.push(e.name));
                            this.groups.forEach(e => this.autocompleteGroups.push(e));
                        }
                    });
            }
        } else {
            this.groups = [];
        }
    }

    // Get Group names in autocomplete code
    getGroupNames(filterValueGroup: string) {
        if (filterValueGroup != null && filterValueGroup.length > 1) {
            let groupTypeId = this.groupTypeControl.value;
            if (groupTypeId != 0) {
                this.groupsService.getGroupNamesAndGroupTypeId(filterValueGroup, groupTypeId).subscribe(groups => {
                        this.groups = groups;
                        this.autocompleteGroupsFormatted = [];
                        this.groups.forEach(e => {
                            this.autocompleteGroupsFormatted.push(e.name);
                            this.groups.forEach(e => this.autocompleteGroups.push(e));
                        });
                    }
                );
            } else {
                this.groupsService.getGroupNames(filterValueGroup).subscribe(groups => {
                    this.groups = groups;
                    this.autocompleteGroupsFormatted = [];
                    this.groups.forEach(e => {
                        this.autocompleteGroupsFormatted.push(e.name);
                        this.groups.forEach(e => this.autocompleteGroups.push(e));
                    });
                });
            }
        }
        setTimeout(() => {
            this.inputAutocomplete.openPanel();
        }, 0);
    }

    buildMPLSJson(message: Message) {
        message.recipients = [];
        // Split common separated list & remove formatting characters
        this.recipients.forEach(recipient =>
            message.recipients.push(new Recipient(recipient.recipient_type, recipient.recipient_id, recipient.response_required, recipient.name))
        );
        return message;
    }

    parseReply(recipients: Recipient[]) {
        recipients.forEach(rec => {
            if (rec.group !== 'Unknown Recipient(s)') {
                if (rec.escalate_from_id === undefined) {
                    // only add original recipients
                    if (rec.super_group_id !== undefined) {
                        this.addGroupReply(rec);
                    }
                    if (rec.super_group_id === undefined) {
                        if (rec.group_id !== undefined) {
                            this.addGroupReply(rec);
                        } else {
                            this.addIndividualReply(rec);
                        }
                    }
                }
            }
        });
    }

    openPopup(event: any) {
        let memberId;
        let memberType;
        if (event.member_type) {
            memberId = event.member_id;
            memberType = event.member_type;
        } else {
            memberId = event.recipient_type ? event.recipient_id : null;
            memberType = event.recipient_type ? event.recipient_type : null;
        }
        switch (memberType) {
            case 'INDIVIDUAL':
                this.openIndividualPopup(memberId);
                break;
            case 'DEVICE':
                this.openDevicePopup(memberId);
                break;
            case 'GROUP':
                this.openGroupPopup(memberId);
                break;
            default:
                if (event.full_name != undefined) {
                    this.openIndividualPopup(event.id);
                } else {
                    let popupOpened = false;
                    this.individualsService.getIndividuals(event).subscribe(individuals => {
                        const individuals2 = individuals.filter(individual => individual.full_name === event);
                        if (individuals2.length === 1) {
                            this.openIndividualPopup(individuals2[0].id);
                            popupOpened = true;
                        } else {
                            this.groupsService.getGroupNames(event).subscribe(groups => {
                                groups = groups.filter(group => group.name === event);
                                if (groups.length === 1) {
                                    this.openGroupPopup(groups[0].id);
                                    popupOpened = true;
                                } else {
                                    this.deviceService.getDevicesByName(event).subscribe(devices => {
                                        devices = devices.filter(device => device.name === event);
                                        if (devices.length === 1) {
                                            this.openDevicePopup(devices[0].id);
                                            popupOpened = true;
                                        }
                                        if (!popupOpened) {
                                            this.snackbarService.openSnackBar('Individual/Channel/Group Not Found', true);
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
                break;
        }
    }

    openDevicePopup(deviceID: any) {
        this.deviceService.getDevice(deviceID).subscribe((device: Device) => {
            this.dialog.open(ChipPopupComponent, {
                width: '85%',
                maxHeight: '90vh',
                data: {object: device, type: 'DEVICE'}
            });
        });
    }

    openIndividualPopup(indID: any) {
        this.individualsService.getIndividual(indID).subscribe((individual: Individual) => {
            this.dialog.open(ChipPopupComponent, {
                width: '85%',
                maxHeight: '90vh',
                data: {object: individual, type: 'INDIVIDUAL'}
            });
        });
    }

    openGroupPopup(groupID: any) {
        this.groupsService.getGroup(groupID).subscribe((group: Group) => {
            this.dialog.open(ChipPopupComponent, {
                width: '85%',
                maxHeight: '90vh',
                data: {object: group, type: 'GROUP'}
            });
        });
    }

    private addRecipient(recipient: Recipient, errorMessage: string) {
        if (this.isDuplicate(recipient)) {
            this.snackbarService.openSnackBar(errorMessage, true);
        } else {
            this.recipients.push(recipient);
            this.recipientsChange.emit(this.recipients);
        }
    }
}
